/**
 * Widget flutuante feedback usuário. IA declarada + botão escalação humana.
 * Fable Opção B — sem disfarce.
 */
'use client'

import { AlertCircle, Bot, MessageCircle, Send, User, X } from 'lucide-react'
import { useState } from 'react'

interface Response {
  ai_response: string | null
  escalated: boolean
  sensitive_topic: boolean
  message_human_wait: string | null
}

export function FeedbackWidget() {
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [response, setResponse] = useState<Response | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function submit(escalate: boolean) {
    if (!message.trim() || loading) return
    setLoading(true)
    setError(null)
    setResponse(null)
    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, escalate }),
      })
      const data = await res.json()
      if (!res.ok) {
        if (data.error === 'rate_limited') {
          setError(`Limite ${data.limit} mensagens/dia atingido.`)
        } else if (data.error === 'not_authenticated') {
          setError('Faça login pra enviar feedback.')
        } else {
          setError('Erro ao enviar. Tente novamente.')
        }
        return
      }
      setResponse(data)
    } catch {
      setError('Erro de rede.')
    } finally {
      setLoading(false)
    }
  }

  function reset() {
    setMessage('')
    setResponse(null)
    setError(null)
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-20 md:bottom-6 right-4 z-30 h-12 w-12 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg flex items-center justify-center"
        aria-label="Abrir feedback"
      >
        <MessageCircle className="h-5 w-5" />
      </button>
    )
  }

  return (
    <div className="fixed bottom-20 md:bottom-6 right-4 z-40 w-[calc(100%-2rem)] sm:w-96 max-w-md rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 shadow-2xl">
      <header className="flex items-center justify-between px-4 py-3 border-b border-zinc-100 dark:border-zinc-900">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-blue-500" />
          <span className="text-sm font-semibold">Feedback</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 uppercase tracking-wide">
            IA + humano
          </span>
        </div>
        <button
          type="button"
          onClick={() => {
            setOpen(false)
            reset()
          }}
          className="p-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-900"
        >
          <X className="h-4 w-4 text-zinc-400" />
        </button>
      </header>

      <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
        {!response && (
          <>
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              Sua mensagem vai pra IA na hora. Se preferir, escale pro time humano — responde em até
              24h por email.
            </p>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Sugestão, dúvida ou problema..."
              rows={4}
              disabled={loading}
              maxLength={2000}
              className="w-full rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2 text-sm resize-none"
            />
            {error && (
              <div className="p-2 rounded bg-red-50 dark:bg-red-950/20 border border-red-200 text-xs text-red-700 flex gap-1.5">
                <AlertCircle className="h-3.5 w-3.5 mt-0.5" />
                {error}
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => submit(false)}
                disabled={!message.trim() || loading}
                className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-300 text-white text-xs font-semibold"
              >
                <Send className="h-3.5 w-3.5" />
                {loading ? 'Enviando…' : 'Enviar (IA responde na hora)'}
              </button>
              <button
                type="button"
                onClick={() => submit(true)}
                disabled={!message.trim() || loading}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 text-xs font-medium"
                title="Fila humana, responde em até 24h"
              >
                <User className="h-3.5 w-3.5" />
                Humano
              </button>
            </div>
          </>
        )}

        {response && (
          <div className="space-y-3">
            <div className="rounded-lg bg-zinc-100 dark:bg-zinc-900 p-2 text-xs text-zinc-600 dark:text-zinc-400 italic">
              Você: "{message}"
            </div>
            {response.ai_response && (
              <div className="rounded-lg border border-blue-200 dark:border-blue-800/40 bg-blue-50 dark:bg-blue-950/20 p-3">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Bot className="h-3.5 w-3.5 text-blue-600" />
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">
                    IA de suporte
                  </span>
                </div>
                <p className="text-xs text-zinc-700 dark:text-zinc-300 whitespace-pre-line leading-relaxed">
                  {response.ai_response}
                </p>
              </div>
            )}
            {response.escalated && (
              <div className="rounded-lg border border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-950/20 p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <User className="h-3.5 w-3.5 text-amber-600" />
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                    Fila humana
                  </span>
                </div>
                <p className="text-xs text-zinc-700 dark:text-zinc-300">
                  {response.sensitive_topic
                    ? 'Assunto sensível — encaminhado pra revisão humana automaticamente.'
                    : 'Sua mensagem foi pra fila humana conforme pedido.'}{' '}
                  {response.message_human_wait}
                </p>
              </div>
            )}
            {!response.escalated && response.ai_response && (
              <button
                type="button"
                onClick={() => submit(true)}
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 text-zinc-700 dark:text-zinc-300 text-xs font-medium"
              >
                <User className="h-3.5 w-3.5" />
                Resposta não resolveu? Escalar pra humano
              </button>
            )}
            <button
              type="button"
              onClick={reset}
              className="w-full py-1.5 rounded-lg border border-zinc-300 dark:border-zinc-700 text-xs font-medium"
            >
              Nova mensagem
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
